import { PropsWithChildren, useEffect } from "react";
import { capitalize } from "lodash";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { create } from "zustand";
import useAuthApi from "@services/api/auth/auth.api";
import {
  AuthenticationConfigProps,
  FetchedAuthenticationConfigProps,
  FetchedAuthenticationProps
} from "@src/types/auth.types";

interface AuthStoreProps {
  user?: FetchedAuthenticationProps;
  setUser: (user?: FetchedAuthenticationProps) => void;
  config?: AuthenticationConfigProps;
  setConfig: (config: AuthenticationConfigProps) => void;
}

export const useAuthStore = create<AuthStoreProps>((set) => ({
  setUser: (user?: FetchedAuthenticationProps) => set(() => ({ user })),
  setConfig: (config: AuthenticationConfigProps) => set(() => ({ config }))
}));

const getRegistrationType = (id: string) => {
  if (id.includes("google")) return "google";
  else if (id.startsWith("te")) return "timeedit";

  return id;
};

const mapAuthConfig = ({
  registrations,
  ...config
}: FetchedAuthenticationConfigProps): AuthenticationConfigProps => ({
  ...config,
  registrations: registrations.map(
    ({ id, label, url, ...registration }: any) => ({
      id,
      label,
      ...registration,
      url: url.replace("/#/", "/"),
      name: label || capitalize(id).replace("-", " ").replace("_", " "),
      type: getRegistrationType(id)
    })
  )
});

export default function AuthProvider({ children }: PropsWithChildren) {
  const { setUser, setConfig } = useAuthStore();

  const { useCurrentApi, useConfigApi } = useAuthApi();
  const { data: user, isError } = useCurrentApi({
    gcTime: 0,
    retry: (failureCount, error: any) => {
      // Never retry on Unauthorized.
      if (error.status === 401) return false;
      if (failureCount >= 2) return false;
      return true;
    }
  });

  useEffect(() => {
    if (isError) {
      setUser(undefined);
      return;
    }

    setUser(user);
  }, [setUser, user, isError]);

  const { data: config } = useConfigApi();

  useEffect(() => {
    if (!config) return;
    setConfig(mapAuthConfig(config));
  }, [config, setConfig]);

  // when logged in
  const [searchParams] = useSearchParams();
  const action = searchParams.get("action");
  const { pathname } = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    if (!user) return;

    const { successUrl } = localStorage;
    if (successUrl) {
      localStorage.removeItem("successUrl");
      location.href = successUrl;
      return;
    }

    if ((pathname !== "/login" && pathname !== "/token") || action === "logout")
      return;

    navigate("/");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // We either need the config
  if (!config) return <></>;

  return children;
}
